import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WhiteboardCanvas from "../../whiteboard/components/WhiteboardCanvas";
import EdgeDrawer from "../ui/EdgeDrawer";
import ExamCalculator from "../ExamCalculator";
import FormulaSheet from "../FormulaSheet";
import {
  QuickIconButton,
  PenIcon,
  EraserIcon,
  HighlighterIcon,
  LineIcon,
  PolygonIcon,
  CircleIcon,
  SelectIcon,
  StylusModeIcon,
  GridIcon,
  UndoIcon,
  RedoIcon,
  FullscreenEnterIcon,
  FullscreenExitIcon,
  ZoomControl,
} from "../../whiteboard/components/WhiteboardControlPrimitives";
import {
  fetchWhiteboardPrefs,
  normalizeWhiteboardPrefs,
  patchWhiteboardPrefs,
} from "../../whiteboard/api/preferences";
import { AuthContext } from "../AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import MatematronModal from "../ui/MatematronModal";

const DEFAULT_PAGE_COUNT = 1;
const MAX_PAGE_COUNT = 100;
const WZORY_URL = process.env.PUBLIC_URL + "/assets/wybrane_wzory_matematyczne_EM2023.pdf";
const openInlineWhiteboardRegistry = new Map();
const INLINE_WHITEBOARD_PREFS_STORAGE_KEY = "whiteboard.userPrefs.inline.v1";
const INLINE_STYLUS_ONLY_STORAGE_KEY = "whiteboard.stylusOnly.inline.v1";
const INLINE_PEN_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6", "#f8fafc", "#000000"];
const INLINE_HIGHLIGHTER_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6"];
const BRUSH_TOOLS = ["pen", "eraser", "highlighter"];
const SHAPE_TOOLS = ["line", "polygon", "circle"];
const BASIC_SIDE_TOOLS = ["select", "pen", "eraser"];

function isEditableTarget(target) {
  const tagName = String(target?.tagName || "").toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || !!target?.isContentEditable;
}

function createInitialState() {
  return {
    version: 1,
    elements: [],
    pageCount: DEFAULT_PAGE_COUNT,
    undoStack: [],
    redoStack: [],
    updatedAt: Date.now(),
    updatedBy: "task-runner-local",
  };
}

function normalizeBoardState(source) {
  const state = source && typeof source === "object" ? source : {};
  return {
    version: Number(state.version || 1),
    elements: cloneElements(state.elements),
    pageCount: Math.max(1, Math.min(MAX_PAGE_COUNT, Number(state.pageCount || DEFAULT_PAGE_COUNT))),
    undoStack: Array.isArray(state.undoStack) ? state.undoStack.map(cloneElements) : [],
    redoStack: Array.isArray(state.redoStack) ? state.redoStack.map(cloneElements) : [],
    updatedAt: Number(state.updatedAt || Date.now()),
    updatedBy: state.updatedBy || "task-runner-local",
  };
}

function cloneElements(elements) {
  return Array.isArray(elements) ? JSON.parse(JSON.stringify(elements)) : [];
}

function CustomColorPickerButton({ value, onChange, size = 30, active = false }) {
  const safeValue = /^#[0-9a-f]{6}$/i.test(String(value || "")) ? String(value) : "#f59e0b";
  return (
    <label
      className="btn btn-secondary"
      title="Własny kolor"
      aria-label="Własny kolor"
      style={{
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        padding: 0,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        fontWeight: 900,
        lineHeight: 1,
        cursor: "pointer",
        overflow: "hidden",
        borderColor: active ? "#f59e0b" : undefined,
        boxShadow: active ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      ⋯
      <input
        type="color"
        value={safeValue}
        onChange={(event) => onChange?.(event.target.value)}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
      />
    </label>
  );
}

function RecentCustomColorButton({ color, active, onSelect, size = 30, palette = [] }) {
  if (!/^#[0-9a-f]{6}$/i.test(String(color || ""))) return null;
  if (palette.some((paletteColor) => String(paletteColor).toLowerCase() === String(color).toLowerCase())) return null;
  return (
    <button
      type="button"
      className="btn btn-secondary"
      title={`Ostatni własny kolor ${color}`}
      aria-label={`Ostatni własny kolor ${color}`}
      onClick={() => onSelect?.(color)}
      onContextMenu={(event) => event.preventDefault()}
      style={{
        width: size,
        height: size,
        minWidth: size,
        padding: 0,
        borderRadius: 999,
        background: color,
        borderColor: active ? "#f59e0b" : "rgba(255,255,255,0.24)",
        boxShadow: active ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    />
  );
}

function pushHistory(stack, elements, limit = 80) {
  const next = [...(Array.isArray(stack) ? stack : []), cloneElements(elements)];
  return next.slice(-limit);
}


function BoardToggleIcon({ open }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="11" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      </svg>
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d={open ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 7.5L19 16.5H5z" fill="currentColor" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 16.5L5 7.5h14z" fill="currentColor" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M10.7 3h2.6l.4 2.1a7 7 0 0 1 1.7.7l1.9-1.1 1.8 1.8-1.1 1.9c.3.5.6 1.1.7 1.7l2.1.4v2.6l-2.1.4a7 7 0 0 1-.7 1.7l1.1 1.9-1.8 1.8-1.9-1.1a7 7 0 0 1-1.7.7L13.3 21h-2.6l-.4-2.1a7 7 0 0 1-1.7-.7l-1.9 1.1-1.8-1.8 1.1-1.9a7 7 0 0 1-.7-1.7L3 13.3v-2.6l2.1-.4a7 7 0 0 1 .7-1.7L4.7 6.7l1.8-1.8 1.9 1.1a7 7 0 0 1 1.7-.7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M9 7V5h6v2M8 7l.7 12h6.6L16 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 11v5M13.5 11v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CalculatorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="7" y="6" width="10" height="3" rx="1" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="15.5" r="1" fill="currentColor" />
      <circle cx="12" cy="15.5" r="1" fill="currentColor" />
      <circle cx="15" cy="15.5" r="1" fill="currentColor" />
    </svg>
  );
}

function FormulaIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="3.5" width="14" height="17" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8h8M8 11.5h8M8 15h6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ToolsMenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 7h14M5 12h14M5 17h14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="9" cy="7" r="1.7" fill="currentColor" />
      <circle cx="15" cy="12" r="1.7" fill="currentColor" />
      <circle cx="11" cy="17" r="1.7" fill="currentColor" />
    </svg>
  );
}

export default function TaskInlineWhiteboard({
  renderTaskPreview,
  alignRight = false,
  initialBoardState = null,
  onBoardStateChange,
  resetToken = 0,
  singleOpen = true,
  openGroup = "inline-whiteboard",
  defaultOpen = false,
  hideToggle = false,
  toggleDescription,
  readOnly = false,
  defaultSideToolsExpanded = false,
  extraSideButtonDefs = [],
  maxInlineBoardHeight = 0,
  preferFixedWidthOverride = null,
  bottomLeftTopControls = null,
}) {
  const { user } = useContext(AuthContext) || {};
  const { theme } = useContext(ThemeContext) || {};
  const themeMode = theme === "light" ? "light" : "dark";

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [isRibbonOpen, setIsRibbonOpen] = useState(false);
  const [controlsSide, setControlsSide] = useState("right");
  const [state, setState] = useState(() => normalizeBoardState(initialBoardState || createInitialState()));
  const [tool, setTool] = useState("select");
  const [penColor, setPenColor] = useState("#f59e0b");
  const [highlighterColor, setHighlighterColor] = useState("#f59e0b");
  const [lastCustomColor, setLastCustomColor] = useState("");
  const [brushSize, setBrushSize] = useState(5);
  const [highlighterSize, setHighlighterSize] = useState(14);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [currentPage, setCurrentPage] = useState(0);
  const [windowHeight, setWindowHeight] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 900));
  const [containerWidth, setContainerWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  const [containerHeight, setContainerHeight] = useState(() => (typeof window !== "undefined" ? Math.max(420, Math.round(window.innerHeight * 0.72)) : 640));
  const [taskPreviewHeight, setTaskPreviewHeight] = useState(0);
  const [taskPreviewExpanded, setTaskPreviewExpanded] = useState(true);
  const [taskPreviewFullHeight, setTaskPreviewFullHeight] = useState(false);
  const [taskPreviewOverflow, setTaskPreviewOverflow] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [boardSurface, setBoardSurface] = useState("default");
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [prefsReady, setPrefsReady] = useState(false);
  const [gridEnabled, setGridEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("whiteboard.gridEnabled");
    return stored === null ? true : stored === "1";
  });
  const [stylusOnly, setStylusOnly] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(INLINE_STYLUS_ONLY_STORAGE_KEY) === "1";
  });
  const [brushGroupOpen, setBrushGroupOpen] = useState(false);
  const [shapeGroupOpen, setShapeGroupOpen] = useState(false);
  const [sideToolsExpanded, setSideToolsExpanded] = useState(defaultSideToolsExpanded);
  const [quickToolConfigTool, setQuickToolConfigTool] = useState("");
  const [lastBrushTool, setLastBrushTool] = useState("pen");
  const [lastShapeTool, setLastShapeTool] = useState("line");

  const fsRef = useRef(null);
  const taskPreviewRef = useRef(null);
  const taskPreviewBodyRef = useRef(null);
  const prefsSaveTimerRef = useRef(null);
  const onBoardStateChangeRef = useRef(onBoardStateChange);
  const uidRef = useRef(Math.random().toString(36).slice(2));
  const zoomLevels = useMemo(() => [100, 110, 125, 150, 175, 200, 250, 300, 350, 400], []);
  const taskPreviewNode = useMemo(() => (renderTaskPreview ? renderTaskPreview() : null), [renderTaskPreview]);
  const hasTaskPreviewNode = taskPreviewNode !== null && taskPreviewNode !== undefined && taskPreviewNode !== false;
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return !!(
      navigator.maxTouchPoints > 0 ||
      "ontouchstart" in window ||
      window.matchMedia?.("(pointer: coarse)")?.matches
    );
  }, []);
  const isAdminUser = useMemo(() => {
    const role = String(user?.role || user?.accountType || "").toLowerCase();
    return role === "admin" || role === "administrator" || user?.isAdmin === true;
  }, [user]);
  const showStylusToggle = isTouchDevice || isAdminUser;

  useEffect(() => {
    onBoardStateChangeRef.current = onBoardStateChange;
  }, [onBoardStateChange]);

  useEffect(() => {
    if (!singleOpen) return undefined;
    const groupName = openGroup || "inline-whiteboard";
    const currentUid = uidRef.current;

    if (isOpen) {
      const prev = openInlineWhiteboardRegistry.get(groupName);
      if (prev && prev.uid !== currentUid) {
        prev.close?.();
      }
      openInlineWhiteboardRegistry.set(groupName, {
        uid: currentUid,
        close: () => setIsOpen(false),
      });
    } else {
      const current = openInlineWhiteboardRegistry.get(groupName);
      if (current && current.uid === currentUid) {
        openInlineWhiteboardRegistry.delete(groupName);
      }
    }

    return () => {
      const current = openInlineWhiteboardRegistry.get(groupName);
      if (current && current.uid === currentUid) {
        openInlineWhiteboardRegistry.delete(groupName);
      }
    };
  }, [isOpen, singleOpen, openGroup]);

  useEffect(() => {
    setState(normalizeBoardState(initialBoardState || createInitialState()));
  }, [resetToken]);

  const emitBoardStateChange = useCallback((nextState) => {
    onBoardStateChangeRef.current?.(normalizeBoardState(nextState));
  }, []);

  useEffect(() => {
    emitBoardStateChange(state);
  }, [emitBoardStateChange, state]);

  const fullscreenActive = isFullscreen || isPseudoFullscreen;

  useEffect(() => {
    if (fullscreenActive) return;
    setCalcOpen(false);
    setSheetOpen(false);
  }, [fullscreenActive]);

  useEffect(() => {
    if (isOpen) return;
    setCalcOpen(false);
    setSheetOpen(false);
  }, [isOpen]);

  async function toggleFullscreen() {
    if (fullscreenActive) {
      setIsPseudoFullscreen(false);
      setIsFullscreen(false);
      return;
    }
    // Force pseudo fullscreen for inline whiteboard so portal drawers
    // (calculator/formula sheet) stay visible and interactive.
    setIsPseudoFullscreen(true);
    setIsFullscreen(false);
  }

  useEffect(() => {
    function handleFullscreenChange() {
      const el = fsRef.current;
      setIsFullscreen(Boolean(el && document.fullscreenElement === el));
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!fullscreenActive || typeof document === "undefined") return undefined;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0;

    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyRight = body.style.right;
    const prevBodyWidth = body.style.width;
    const prevBodyOverscroll = body.style.overscrollBehavior;

    const prevHtmlOverflow = html?.style?.overflow;
    const prevHtmlOverscroll = html?.style?.overscrollBehavior;

    document.body.classList.add("inline-whiteboard-fullscreen-active");
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overscrollBehavior = "none";
    if (html) {
      html.style.overflow = "hidden";
      html.style.overscrollBehavior = "none";
    }

    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.right = prevBodyRight;
      body.style.width = prevBodyWidth;
      body.style.overscrollBehavior = prevBodyOverscroll;
      if (html) {
        html.style.overflow = prevHtmlOverflow;
        html.style.overscrollBehavior = prevHtmlOverscroll;
      }
      document.body.classList.remove("inline-whiteboard-fullscreen-active");
      if (typeof window !== "undefined") {
        window.scrollTo(0, scrollY);
      }
    };
  }, [fullscreenActive]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    function handleResize() {
      const vvHeight = Number(window.visualViewport?.height || 0);
      setWindowHeight(vvHeight > 0 ? vvHeight : window.innerHeight || 900);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener?.("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener?.("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const el = fsRef.current;
    if (!el) return undefined;

    const update = () => {
      const rect = el.getBoundingClientRect?.();
      const w = Math.round(rect?.width || 0);
      const h = Math.round(rect?.height || 0);
      if (w > 0) setContainerWidth(w);
      if (h > 0) setContainerHeight(h);
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
      }
      return undefined;
    }

    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (typeof window !== "undefined") window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      if (typeof window !== "undefined") window.removeEventListener("resize", update);
    };
  }, [isOpen, fullscreenActive, isPseudoFullscreen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("whiteboard.gridEnabled", gridEnabled ? "1" : "0");
  }, [gridEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(INLINE_STYLUS_ONLY_STORAGE_KEY, stylusOnly ? "1" : "0");
  }, [stylusOnly]);

  useEffect(() => {
    let cancelled = false;

    async function loadPrefs() {
      try {
        if (typeof window !== "undefined") {
          const raw = window.localStorage.getItem(INLINE_WHITEBOARD_PREFS_STORAGE_KEY);
          if (raw) {
            const parsed = normalizeWhiteboardPrefs(JSON.parse(raw));
            if (!cancelled) {
              setPenColor(parsed.color);
              if (INLINE_HIGHLIGHTER_COLORS.includes(parsed.color)) {
                setHighlighterColor(parsed.color);
              }
              setBrushSize(parsed.penSize);
              setHighlighterSize(parsed.highlighterSize);
              setBoardSurface(parsed.boardSurface);
              setGridEnabled(parsed.gridEnabled);
            }
          }
        }
      } catch {
        // ignore local fallback errors
      }

      try {
        const serverPrefs = await fetchWhiteboardPrefs();
        if (!cancelled) {
          setPenColor(serverPrefs.color);
          if (INLINE_HIGHLIGHTER_COLORS.includes(serverPrefs.color)) {
            setHighlighterColor(serverPrefs.color);
          }
          setBrushSize(serverPrefs.penSize);
          setHighlighterSize(serverPrefs.highlighterSize);
          setBoardSurface(serverPrefs.boardSurface);
          setGridEnabled(serverPrefs.gridEnabled);
          if (typeof window !== "undefined") {
            window.localStorage.setItem(INLINE_WHITEBOARD_PREFS_STORAGE_KEY, JSON.stringify(serverPrefs));
          }
        }
      } catch {
        // silent fallback to local/default
      } finally {
        if (!cancelled) setPrefsReady(true);
      }
    }

    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!prefsReady) return undefined;
    if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);

    const payload = {
      color: penColor,
      penSize: brushSize,
      highlighterSize,
      boardSurface,
      gridEnabled,
    };

    prefsSaveTimerRef.current = setTimeout(() => {
      patchWhiteboardPrefs(payload).catch(() => {});
      if (typeof window !== "undefined") {
        window.localStorage.setItem(INLINE_WHITEBOARD_PREFS_STORAGE_KEY, JSON.stringify(payload));
      }
      prefsSaveTimerRef.current = null;
    }, 600);

    return () => {
      if (prefsSaveTimerRef.current) {
        clearTimeout(prefsSaveTimerRef.current);
        prefsSaveTimerRef.current = null;
      }
    };
  }, [prefsReady, penColor, brushSize, highlighterSize, boardSurface, gridEnabled]);

  useEffect(() => {
    if (!fullscreenActive || !hasTaskPreviewNode) {
      setTaskPreviewHeight(0);
      return undefined;
    }
    const el = taskPreviewRef.current;
    if (!el) return undefined;
    const update = () => setTaskPreviewHeight(Math.ceil(el.getBoundingClientRect().height || 0));
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [fullscreenActive, hasTaskPreviewNode, taskPreviewNode, themeMode, taskPreviewExpanded, windowHeight]);

  useEffect(() => {
    if (!fullscreenActive || !hasTaskPreviewNode || !taskPreviewExpanded) {
      setTaskPreviewOverflow(false);
      return undefined;
    }
    if (taskPreviewFullHeight) {
      return undefined;
    }
    const el = taskPreviewBodyRef.current;
    if (!el) {
      setTaskPreviewOverflow(false);
      return undefined;
    }

    const updateOverflow = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      setTaskPreviewOverflow(hasOverflow);
    };

    updateOverflow();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    window.addEventListener("resize", updateOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [fullscreenActive, hasTaskPreviewNode, taskPreviewExpanded, taskPreviewFullHeight, taskPreviewNode, themeMode, windowHeight]);

  function zoomIn() {
    setZoomPercent((prev) => {
      const idx = zoomLevels.indexOf(prev);
      if (idx < 0) return 100;
      return zoomLevels[Math.min(zoomLevels.length - 1, idx + 1)];
    });
  }

  function zoomOut() {
    setZoomPercent((prev) => {
      const idx = zoomLevels.indexOf(prev);
      if (idx <= 0) return zoomLevels[0];
      return zoomLevels[idx - 1];
    });
  }

  function handleBoardChange(nextState, options = {}) {
    if (readOnly) return;
    setState((prev) => {
      const base = {
        ...prev,
        pageCount: Math.max(1, Math.min(MAX_PAGE_COUNT, Number(nextState?.pageCount || prev.pageCount || DEFAULT_PAGE_COUNT))),
      };
      const nextElements = cloneElements(nextState?.elements);

      if (options.history === "undo") {
        if (!base.undoStack?.length) return prev;
        const prevElements = base.undoStack[base.undoStack.length - 1];
        return {
          ...base,
          version: base.version + 1,
          elements: cloneElements(prevElements),
          undoStack: base.undoStack.slice(0, -1),
          redoStack: pushHistory(base.redoStack, base.elements),
          updatedAt: Date.now(),
        };
      }

      if (options.history === "redo") {
        if (!base.redoStack?.length) return prev;
        const redoElements = base.redoStack[base.redoStack.length - 1];
        return {
          ...base,
          version: base.version + 1,
          elements: cloneElements(redoElements),
          undoStack: pushHistory(base.undoStack, base.elements),
          redoStack: base.redoStack.slice(0, -1),
          updatedAt: Date.now(),
        };
      }

      if (options.history === "skip") {
        return {
          ...base,
          version: base.version + 1,
          elements: nextElements,
          updatedAt: Date.now(),
        };
      }

      const same = JSON.stringify(base.elements) === JSON.stringify(nextElements);
      if (same) return prev;
      return {
        ...base,
        version: base.version + 1,
        elements: nextElements,
        undoStack: pushHistory(base.undoStack, base.elements),
        redoStack: [],
        updatedAt: Date.now(),
      };
    });
  }

  function handleUndo() {
    handleBoardChange(state, { history: "undo" });
  }

  function handleRedo() {
    handleBoardChange(state, { history: "redo" });
  }

  function handleAddPage() {
    if (Number(state.pageCount || DEFAULT_PAGE_COUNT) >= MAX_PAGE_COUNT) return;
    handleBoardChange(
      {
        ...state,
        pageCount: Number(state.pageCount || DEFAULT_PAGE_COUNT) + 1,
      },
      { history: "skip" }
    );
  }

  function clearBoardNow() {
    handleBoardChange({ ...state, elements: [] });
  }

  const canUndo = (state.undoStack || []).length > 0;
  const canRedo = (state.redoStack || []).length > 0;
  const isBoardEmpty = !Array.isArray(state.elements) || state.elements.length === 0;
  const isBrushTool = tool === "pen" || tool === "highlighter" || tool === "line" || tool === "polygon" || tool === "circle";
  const penPalette = INLINE_PEN_COLORS;
  const highlighterPalette = INLINE_HIGHLIGHTER_COLORS;
  const activePalette = tool === "highlighter" ? highlighterPalette : penPalette;
  const activeColor = tool === "highlighter" ? highlighterColor : penColor;
  const activeSize = tool === "highlighter" ? highlighterSize : brushSize;
  const touchSafeButtonStyle = {
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  };

  const isQuickConfigurableTool = useCallback(
    (toolName) => ["pen", "highlighter", "line", "polygon", "circle"].includes(String(toolName || "")),
    []
  );

  const getQuickToolColor = useCallback(
    (toolName) => (String(toolName || "") === "highlighter" ? highlighterColor : penColor),
    [highlighterColor, penColor]
  );

  const setQuickToolColor = useCallback((toolName, nextColor) => {
    if (String(toolName || "") === "highlighter") setHighlighterColor(nextColor);
    else setPenColor(nextColor);
  }, []);

  const getQuickToolSize = useCallback(
    (toolName) => (String(toolName || "") === "highlighter" ? highlighterSize : brushSize),
    [highlighterSize, brushSize]
  );

  const setQuickToolSize = useCallback((toolName, nextSize) => {
    const size = Math.max(1, Number(nextSize || 1));
    if (String(toolName || "") === "highlighter") setHighlighterSize(size);
    else setBrushSize(size);
  }, []);

  useEffect(() => {
    if (!quickToolConfigTool) return;
    if (isQuickConfigurableTool(tool)) {
      setQuickToolConfigTool(tool);
      return;
    }
    setQuickToolConfigTool("");
  }, [tool, quickToolConfigTool, isQuickConfigurableTool]);

  useEffect(() => {
    if (BRUSH_TOOLS.includes(tool)) setLastBrushTool(tool);
    if (SHAPE_TOOLS.includes(tool)) setLastShapeTool(tool);
  }, [tool]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;
      if (isEditableTarget(e.target)) return;

      const key = String(e.key || "").toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;

      if (key === "escape") {
        e.preventDefault();
        if (tool !== "select") {
          setTool("select");
          return;
        }
        if (fullscreenActive) {
          setIsPseudoFullscreen(false);
          setIsFullscreen(false);
        }
        return;
      }

      if (ctrlOrMeta) {
        if (key === "z" && !e.shiftKey) {
          if (canUndo) {
            e.preventDefault();
            handleUndo();
          }
          return;
        }

        if (key === "y" || (key === "z" && e.shiftKey)) {
          if (canRedo) {
            e.preventDefault();
            handleRedo();
          }
          return;
        }

        if (key === "+" || key === "=") {
          e.preventDefault();
          zoomIn();
          return;
        }

        if (key === "-" || key === "_") {
          e.preventDefault();
          zoomOut();
          return;
        }

        if (key === "0") {
          e.preventDefault();
          setZoomPercent(100);
          return;
        }
      }

      if (e.altKey || ctrlOrMeta) return;

      if (key === "p") {
        e.preventDefault();
        setTool("pen");
        return;
      }

      if (key === "s") {
        e.preventDefault();
        setTool("select");
        return;
      }

      if (key === "l") {
        e.preventDefault();
        setTool("highlighter");
        return;
      }

      if (key === "e") {
        e.preventDefault();
        setTool("eraser");
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, canUndo, canRedo, zoomLevels, fullscreenActive, tool]);

  const ribbonPanel = isRibbonOpen ? (
    <div
      style={{
        display: "grid",
        gap: 10,
        borderRadius: 16,
        border: themeMode === "light" ? "1px solid rgba(15,23,42,0.14)" : "1px solid rgba(255,255,255,0.14)",
        background: themeMode === "light" ? "rgba(225,243,252,0.98)" : "rgba(12,16,40,0.88)",
        color: themeMode === "light" ? "#0f172a" : "#f8fafc",
        padding: 12,
      }}
    >
      <div style={{ fontWeight: 900, fontSize: 14 }}>Ustawienia tablicy</div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <QuickIconButton
          label={<PenIcon />}
          title="Pióro"
          active={tool === "pen"}
          indicatorColor={penColor}
          onClick={() => setTool("pen")}
        />
        <QuickIconButton
          label={<HighlighterIcon />}
          title="Zakreślacz"
          active={tool === "highlighter"}
          indicatorColor={highlighterColor}
          onClick={() => setTool("highlighter")}
        />
        <QuickIconButton
          label={<LineIcon />}
          title="Linia"
          active={tool === "line"}
          indicatorColor={penColor}
          onClick={() => setTool("line")}
        />
        <QuickIconButton
          label={<PolygonIcon />}
          title="Wielokąt"
          active={tool === "polygon"}
          indicatorColor={penColor}
          onClick={() => setTool("polygon")}
        />
        <QuickIconButton
          label={<CircleIcon />}
          title="Kolko"
          active={tool === "circle"}
          indicatorColor={penColor}
          onClick={() => setTool("circle")}
        />
        <QuickIconButton
          label={<TrashIcon />}
          title={isBoardEmpty ? "Tablica jest pusta" : "Wyczyść tablicę"}
          disabled={isBoardEmpty}
          onClick={() => {
            if (!isBoardEmpty) setClearConfirmOpen(true);
          }}
        />
      </div>

      {showStylusToggle ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Tryb awaryjny</div>
          <QuickIconButton
            label={<StylusModeIcon />}
            title={stylusOnly ? "Tryb rysika: wlaczony" : "Tryb rysika: wylaczony"}
            active={stylusOnly}
            onClick={() => setStylusOnly((v) => !v)}
          />
          <span style={{ fontSize: 12, opacity: 0.76 }}>
            Wlacz, gdy tablet lapie dotyk dloni zamiast samego rysika.
          </span>
        </div>
      ) : null}

      {isBrushTool ? (
        <>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {activePalette.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                if (tool === "highlighter") setHighlighterColor(c);
                else setPenColor(c);
              }}
              onContextMenu={(e) => e.preventDefault()}
              title={c}
              aria-label={`Kolor ${c}`}
              className="btn btn-secondary"
              style={{
                width: 30,
                height: 30,
                minWidth: 30,
                borderRadius: 999,
                padding: 0,
                background: c,
                borderColor: activeColor === c ? "#f59e0b" : "rgba(255,255,255,0.24)",
                boxShadow: activeColor === c ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
                ...touchSafeButtonStyle,
              }}
            />
          ))}
          <RecentCustomColorButton
            color={lastCustomColor}
            palette={activePalette}
            active={String(activeColor).toLowerCase() === String(lastCustomColor).toLowerCase()}
            onSelect={(nextColor) => {
              if (tool === "highlighter") setHighlighterColor(nextColor);
              else setPenColor(nextColor);
            }}
          />
          <CustomColorPickerButton
            value={activeColor}
            active={!activePalette.some((paletteColor) => paletteColor.toLowerCase() === String(activeColor).toLowerCase())}
            onChange={(nextColor) => {
              setLastCustomColor(nextColor);
              if (tool === "highlighter") setHighlighterColor(nextColor);
              else setPenColor(nextColor);
            }}
          />
        </div>
        <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13, fontWeight: 700 }}>
            Grubość
            <input
              type="range"
              min={tool === "highlighter" ? 8 : 2}
              max={tool === "highlighter" ? 36 : 24}
              step={1}
              value={activeSize}
              onChange={(e) => {
                const nextValue = Number(e.target.value);
                if (tool === "highlighter") setHighlighterSize(nextValue);
                else setBrushSize(nextValue);
              }}
            />
            <span style={{ minWidth: 18, textAlign: "right" }}>{activeSize}</span>
          </label>
        </>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Tło</div>
        {[
          { id: "default", color: themeMode === "light" ? "rgba(175,236,196,0.98)" : "rgba(16,70,48,0.98)", title: "Domyślne" },
          { id: "white", color: "#ffffff", title: "Białe" },
          { id: "black", color: "#0b0f12", title: "Czarne" },
        ].map((option) => (
          <button
            key={option.id}
            type="button"
            title={option.title}
            aria-label={option.title}
            onClick={() => setBoardSurface(option.id)}
            onContextMenu={(e) => e.preventDefault()}
            className="btn btn-secondary"
            style={{
              width: 32,
              height: 32,
              minWidth: 32,
              borderRadius: 999,
              padding: 0,
              background: option.color,
              borderColor: boardSurface === option.id ? "#f59e0b" : "rgba(255,255,255,0.26)",
              boxShadow: boardSurface === option.id ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
              ...touchSafeButtonStyle,
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ marginLeft: "auto" }}>
          <QuickIconButton label={<ChevronUpIcon />} title="Schowaj ustawienia" onClick={() => setIsRibbonOpen(false)} />
        </div>
      </div>
    </div>
  ) : null;

  const isMobileNarrow = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  // W quizie kontener tablicy bywa wyraźnie węższy niż całe okno.
  // Dlatego tryb kompaktowy przełączamy także po realnej szerokości kontenera.
  const isContainerNarrow = containerWidth <= 980;
  const useCompactControlsLayout = isMobileNarrow || isContainerNarrow;
  const compactButtonSizePx = 34;
  const compactButtonGapPx = 6;
  const useOverlayCompactControls = false;
  const showExternalCompactControls = useCompactControlsLayout;
  const externalCompactControlsHeightPx = showExternalCompactControls ? compactButtonSizePx + 8 : 0;
  const browserBottomInsetPx = fullscreenActive && typeof window !== "undefined"
    ? Math.max(
        0,
        Math.round(
          Math.max(
            (window.innerHeight || 0) - (window.visualViewport?.height || window.innerHeight || 0),
            (window.innerHeight || 0) - ((window.visualViewport?.offsetTop || 0) + (window.visualViewport?.height || window.innerHeight || 0))
          )
        )
      )
    : 0;
  const taskFloatingControlsBottomOffsetPx = fullscreenActive
    ? (useCompactControlsLayout ? Math.min(110, Math.max(0, browserBottomInsetPx + 18)) : 0)
    : 0;
  const fullscreenCanvasOffsetPx = fullscreenActive
    ? taskPreviewHeight + 14 + externalCompactControlsHeightPx
    : 0;
  const inlineMinHeightPx = maxInlineBoardHeight > 0 ? 180 : 220;
  const inlineSinglePageHeightPx = Math.max(
    inlineMinHeightPx,
    Math.round((Math.max(280, containerWidth - 8) * 1000) / 1414)
  );
  const measuredViewportHeightPx = fullscreenActive
    ? Math.max(220, containerHeight - 24 - fullscreenCanvasOffsetPx)
    : maxInlineBoardHeight > 0
      ? maxInlineBoardHeight
      : inlineSinglePageHeightPx;
  const inlineHeightCapPx = maxInlineBoardHeight > 0
    ? maxInlineBoardHeight
    : inlineSinglePageHeightPx;
  const viewportHeightPx = fullscreenActive
    ? measuredViewportHeightPx
    : Math.max(inlineMinHeightPx, Math.min(measuredViewportHeightPx, inlineHeightCapPx));
  const preferFixedWidthForCanvas =
    typeof preferFixedWidthOverride === "boolean"
      ? preferFixedWidthOverride
      : (fullscreenActive || showExternalCompactControls);
  const bottomButtonCount = 1 + 1 + 1 + (zoomPercent > 100 ? 1 : 0) + 1; // grid + up + down + right + fullscreen
  const bottomReservedPx = bottomButtonCount * (compactButtonSizePx + compactButtonGapPx) + 16;
  const topReservedPx = 68;
  const availableSideHeightPx = Math.max(120, viewportHeightPx - topReservedPx - bottomReservedPx);
  const availableSideHeightSecondaryPx = Math.max(120, viewportHeightPx - topReservedPx - 12);
  const sideButtonSlotPx = compactButtonSizePx;
  const sideButtonGapPx = compactButtonGapPx;
  const sideButtonPitchPx = sideButtonSlotPx + sideButtonGapPx;
  const minRowsToKeepHistoryPair = 2;
  const maxRowsPrimaryColumn = Math.max(
    minRowsToKeepHistoryPair,
    Math.floor((availableSideHeightPx + sideButtonGapPx) / sideButtonPitchPx)
  );
  const maxRowsSecondaryColumn = Math.max(
    minRowsToKeepHistoryPair,
    Math.floor((availableSideHeightSecondaryPx + sideButtonGapPx) / sideButtonPitchPx)
  );

  const toggleControlsSide = () => {
      setControlsSide((prev) => (prev === "right" ? "left" : "right"));
    };

    const toggleSideToolsExpanded = () => {
      const next = !sideToolsExpanded;
      if (!next && !BASIC_SIDE_TOOLS.includes(tool)) {
        setTool("select");
      }
      setSideToolsExpanded(next);
    };

    const panelToggleButton = (
      <QuickIconButton
        label={<SettingsIcon />}
        title={isRibbonOpen ? "Ukryj ustawienia" : "Ustawienia tablicy"}
        active={isRibbonOpen}
        onClick={() => setIsRibbonOpen((v) => !v)}
      />
    );

    const controlsSideButton = (
      <QuickIconButton
        label="⇄"
        title={controlsSide === "right" ? "Przenieś panele na lewą stronę" : "Przenieś panele na prawą stronę"}
        onClick={toggleControlsSide}
      />
    );

  const panelControlsStack = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `${compactButtonSizePx}px`,
        columnGap: compactButtonGapPx,
        rowGap: compactButtonGapPx,
        justifyContent: controlsSide === "left" ? "end" : "start",
        alignItems: "start",
      }}
    >
      {panelToggleButton}
      {controlsSideButton}
      <QuickIconButton
        label={<CalculatorIcon />}
        title="Kalkulator"
        active={calcOpen}
        onClick={() => setCalcOpen((v) => !v)}
      />
      <QuickIconButton
        label={<FormulaIcon />}
        title="Karta wzorow"
        active={sheetOpen}
        onClick={() => setSheetOpen((v) => !v)}
      />
    </div>
  );

  const zoomControlsBlock = (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: isMobileNarrow ? "flex-start" : "flex-end",
        maxWidth: "100%",
      }}
    >
      <ZoomControl
        zoomPercent={zoomPercent}
        onZoomOut={zoomOut}
        onReset={() => setZoomPercent(100)}
        onZoomIn={zoomIn}
        minZoom={100}
        maxZoom={400}
        themeMode={themeMode}
      />
      <QuickIconButton
        label={<ToolsMenuIcon />}
        title={sideToolsExpanded ? "Zwin szybkie narzedzia" : "Rozwin pelne narzedzia"}
        active={sideToolsExpanded}
        onClick={toggleSideToolsExpanded}
      />
    </div>
  );

  const topLeftControls = controlsSide === "left" ? zoomControlsBlock : panelControlsStack;

  const topZoomControls = controlsSide === "left" ? panelControlsStack : zoomControlsBlock;

  const getToolButtonMeta = (toolName) => {
    if (toolName === "pen") return { label: <PenIcon />, title: "Pióro", indicatorColor: penColor };
    if (toolName === "eraser") return { label: <EraserIcon />, title: "Gumka" };
    if (toolName === "highlighter") return { label: <HighlighterIcon />, title: "Zakreślacz", indicatorColor: highlighterColor };
    if (toolName === "line") return { label: <LineIcon />, title: "Linia", indicatorColor: penColor };
    if (toolName === "polygon") return { label: <PolygonIcon />, title: "Wielokąt", indicatorColor: penColor };
    if (toolName === "circle") return { label: <CircleIcon />, title: "Kółko", indicatorColor: penColor };
    return { label: <SelectIcon />, title: "Wybierz" };
  };

  const quickConfigTool = isQuickConfigurableTool(quickToolConfigTool) ? quickToolConfigTool : "";
  const quickConfigPalette = quickConfigTool === "highlighter" ? INLINE_HIGHLIGHTER_COLORS : INLINE_PEN_COLORS;
  const quickConfigColor = quickConfigTool ? getQuickToolColor(quickConfigTool) : "";
  const quickConfigSize = quickConfigTool ? getQuickToolSize(quickConfigTool) : brushSize;
  const quickConfigRangeMin = quickConfigTool === "highlighter" ? 8 : 2;
  const quickConfigRangeMax = quickConfigTool === "highlighter" ? 36 : 24;

  const brushTools = BRUSH_TOOLS;
  const shapeTools = SHAPE_TOOLS;
  const activeBrushTool = brushTools.includes(tool) ? tool : lastBrushTool;
  const activeShapeTool = shapeTools.includes(tool) ? tool : lastShapeTool;
  const brushMeta = getToolButtonMeta(activeBrushTool);
  const shapeMeta = getToolButtonMeta(activeShapeTool);
  const compactGroupToggleStyle = { width: 22, height: 42, minWidth: 22, minHeight: 42, paddingInline: 4 };
  const compactGroupToggleLabel = (icon) => (
    <span style={{ display: "inline-flex", transform: "rotate(270deg)" }}>
      {icon}
    </span>
  );
  // Dodatkowe przyciski boczne (np. w tourze: zdjęcie/wykres/zadanie/link)
  // pokazujemy tylko na pełnym ekranie. W szybkim widoku inline pasek narzędzi
  // ma wyglądać dokładnie jak w tablicy przy zadaniu.
  const normalizedExtraSideButtonDefs = fullscreenActive && Array.isArray(extraSideButtonDefs)
    ? extraSideButtonDefs.filter((btn) => btn && btn.key)
    : [];
  const renderExtraSideButton = (btn, keyPrefix = "extra") => (
    <QuickIconButton
      key={`${keyPrefix}-${btn.key}`}
      label={btn.label}
      title={btn.title}
      active={!!btn.active}
      indicatorColor={btn.indicatorColor}
      disabled={!!btn.disabled}
      minWidth={Number(btn.minWidth || 42)}
      style={btn.style || undefined}
      onClick={btn.onClick}
      cornerActionTitle={btn.cornerActionTitle || ""}
      onCornerAction={btn.onCornerAction}
      cornerActionActive={!!btn.cornerActionActive}
      cornerActionLabel={btn.cornerActionLabel || "..."}
    />
  );
  const compactExtraSideControls = normalizedExtraSideButtonDefs.length ? (
    <div
      style={{
        display: "flex",
        gap: compactButtonGapPx,
        flexWrap: "wrap",
        justifyContent: controlsSide === "left" ? "flex-start" : "flex-end",
        width: "100%",
      }}
    >
      {normalizedExtraSideButtonDefs.map((btn) => renderExtraSideButton(btn, "compact-extra"))}
    </div>
  ) : null;

  const compactTopControls = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: controlsSide === "left" ? "minmax(0, 1fr) auto" : "auto minmax(0, 1fr)",
        gap: 8,
        alignItems: "flex-start",
        maxWidth: "100%",
        width: "100%",
        paddingLeft: 10,
        boxSizing: "border-box",
        direction: "ltr",
      }}
    >
      <div
        style={{
          display: "grid",
          gap: compactButtonGapPx,
          gridTemplateColumns: `${compactButtonSizePx}px`,
          alignItems: fullscreenActive ? "start" : "center",
          justifyContent: "flex-start",
          minWidth: 0,
          width: compactButtonSizePx,
          paddingTop: 2,
          direction: "ltr",
          order: controlsSide === "left" ? 2 : 1,
        }}
      >
      <QuickIconButton
        label={<SettingsIcon />}
        title={isRibbonOpen ? "Ukryj ustawienia" : "Ustawienia tablicy"}
        active={isRibbonOpen}
        onClick={() => setIsRibbonOpen((v) => !v)}
      />
      {controlsSideButton}
      {fullscreenActive ? (
        <>
          <QuickIconButton
            label={<CalculatorIcon />}
            title="Kalkulator"
            active={calcOpen}
            onClick={() => setCalcOpen((v) => !v)}
          />
          <QuickIconButton
            label={<FormulaIcon />}
            title="Karta wzorow"
            active={sheetOpen}
            onClick={() => setSheetOpen((v) => !v)}
          />
        </>
      ) : null}
      </div>
      <div
        style={{
          display: "flex",
          gap: compactButtonGapPx,
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          minWidth: 0,
          width: "100%",
          direction: "ltr",
          order: controlsSide === "left" ? 1 : 2,
        }}
      >
      <ZoomControl
        zoomPercent={zoomPercent}
        onZoomOut={zoomOut}
        onReset={() => setZoomPercent(100)}
        onZoomIn={zoomIn}
        minZoom={100}
        maxZoom={400}
        themeMode={themeMode}
      />
      <QuickIconButton
        label={<ToolsMenuIcon />}
        title={sideToolsExpanded ? "Zwin szybkie narzedzia" : "Rozwin pelne narzedzia"}
        active={sideToolsExpanded}
        onClick={toggleSideToolsExpanded}
      />
      <span aria-hidden="true" style={{ flexBasis: "100%", height: 0 }} />
      {sideToolsExpanded ? (
        <>
      <QuickIconButton label={<SelectIcon />} title="Wybierz" active={tool === "select"} onClick={() => setTool("select")} />
      <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flex: "0 0 auto" }}>
      <QuickIconButton
        label={brushMeta.label}
        title={brushMeta.title}
        active={brushTools.includes(tool)}
        indicatorColor={brushMeta.indicatorColor}
        onClick={() => setTool(activeBrushTool)}
        cornerActionTitle="Szybkie ustawienia"
        onCornerAction={() => {
          setQuickToolConfigTool((prev) => (prev === activeBrushTool ? "" : activeBrushTool));
          setTool(activeBrushTool);
        }}
        cornerActionActive={quickConfigTool === activeBrushTool}
        cornerActionLabel="⋯"
      />
      <QuickIconButton
        label={compactGroupToggleLabel(brushGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />)}
        title={brushGroupOpen ? "Zwiń grupę pisania" : "Rozwiń grupę pisania"}
        active={brushGroupOpen}
        minWidth={42}
        style={compactGroupToggleStyle}
        onClick={() => setBrushGroupOpen((v) => !v)}
      />
      </span>
      {brushGroupOpen ? <span aria-hidden="true" style={{ flexBasis: "100%", height: 0 }} /> : null}
      {brushGroupOpen
        ? brushTools
            .filter((name) => name !== activeBrushTool)
            .map((name) => {
              const meta = getToolButtonMeta(name);
              const hasQuickConfig = isQuickConfigurableTool(name);
              return (
                <QuickIconButton
                  key={`compact-brush-${name}`}
                  label={meta.label}
                  title={meta.title}
                  active={tool === name}
                  indicatorColor={meta.indicatorColor}
                  onClick={() => setTool(name)}
                  cornerActionTitle={hasQuickConfig ? "Szybkie ustawienia" : ""}
                  onCornerAction={
                    hasQuickConfig
                      ? () => {
                          setQuickToolConfigTool((prev) => (prev === name ? "" : name));
                          setTool(name);
                        }
                      : undefined
                  }
                  cornerActionActive={quickConfigTool === name}
                  cornerActionLabel="⋯"
                />
              );
            })
        : null}
      <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flex: "0 0 auto" }}>
      <QuickIconButton
        label={shapeMeta.label}
        title={shapeMeta.title}
        active={shapeTools.includes(tool)}
        indicatorColor={shapeMeta.indicatorColor}
        onClick={() => setTool(activeShapeTool)}
        cornerActionTitle="Szybkie ustawienia"
        onCornerAction={() => {
          setQuickToolConfigTool((prev) => (prev === activeShapeTool ? "" : activeShapeTool));
          setTool(activeShapeTool);
        }}
        cornerActionActive={quickConfigTool === activeShapeTool}
        cornerActionLabel="⋯"
      />
      <QuickIconButton
        label={compactGroupToggleLabel(shapeGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />)}
        title={shapeGroupOpen ? "Zwiń grupę kształtów" : "Rozwiń grupę kształtów"}
        active={shapeGroupOpen}
        minWidth={42}
        style={compactGroupToggleStyle}
        onClick={() => setShapeGroupOpen((v) => !v)}
      />
      </span>
      {shapeGroupOpen ? <span aria-hidden="true" style={{ flexBasis: "100%", height: 0 }} /> : null}
      {shapeGroupOpen
        ? shapeTools
            .filter((name) => name !== activeShapeTool)
            .map((name) => {
              const meta = getToolButtonMeta(name);
              const hasQuickConfig = isQuickConfigurableTool(name);
              return (
                <QuickIconButton
                  key={`compact-shape-${name}`}
                  label={meta.label}
                  title={meta.title}
                  active={tool === name}
                  indicatorColor={meta.indicatorColor}
                  onClick={() => setTool(name)}
                  cornerActionTitle={hasQuickConfig ? "Szybkie ustawienia" : ""}
                  onCornerAction={
                    hasQuickConfig
                      ? () => {
                          setQuickToolConfigTool((prev) => (prev === name ? "" : name));
                          setTool(name);
                        }
                      : undefined
                  }
                  cornerActionActive={quickConfigTool === name}
                  cornerActionLabel="⋯"
                />
              );
            })
        : null}
      <QuickIconButton label={<UndoIcon />} title="Cofnij" disabled={!canUndo} onClick={handleUndo} />
      <QuickIconButton label={<RedoIcon />} title="Ponow" disabled={!canRedo} onClick={handleRedo} />
        </>
      ) : (
        <>
          <QuickIconButton label={<SelectIcon />} title="Wybierz" active={tool === "select"} onClick={() => setTool("select")} />
          {BASIC_SIDE_TOOLS.filter((name) => name !== "select").map((name) => {
            const meta = getToolButtonMeta(name);
            const hasQuickConfig = isQuickConfigurableTool(name);
            return (
              <QuickIconButton
                key={`compact-basic-${name}`}
                label={meta.label}
                title={meta.title}
                active={tool === name}
                indicatorColor={meta.indicatorColor}
                onClick={() => setTool(name)}
                cornerActionTitle={hasQuickConfig ? "Szybkie ustawienia" : ""}
                onCornerAction={
                  hasQuickConfig
                    ? () => {
                        setQuickToolConfigTool((prev) => (prev === name ? "" : name));
                        setTool(name);
                      }
                    : undefined
                }
                cornerActionActive={quickConfigTool === name}
                cornerActionLabel="⋯"
              />
            );
          })}
        </>
      )}
      </div>
    </div>
  );
  const createSideToolDef = (toolName, keyPrefix = "side-tool") => {
    const meta = getToolButtonMeta(toolName);
    const hasQuickConfig = isQuickConfigurableTool(toolName);
    return {
      key: `${keyPrefix}-${toolName}`,
      label: meta.label,
      title: meta.title,
      active: tool === toolName,
      indicatorColor: meta.indicatorColor,
      onClick: () => setTool(toolName),
      cornerActionTitle: hasQuickConfig ? "Szybkie ustawienia" : "",
      onCornerAction: hasQuickConfig
        ? () => {
            setQuickToolConfigTool((prev) => (prev === toolName ? "" : toolName));
            setTool(toolName);
          }
        : undefined,
      cornerActionActive: quickConfigTool === toolName,
      cornerActionLabel: "⋯",
    };
  };

  const sideButtonDefs = {
    select: { key: "select", label: <SelectIcon />, title: "Wybierz", active: tool === "select", onClick: () => setTool("select") },
    brushMain: createSideToolDef(activeBrushTool, "side-brush-main"),
    brushToggle: {
      key: "brush-toggle",
      label: brushGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />,
      title: brushGroupOpen ? "Zwiń grupę pisania" : "Rozwiń grupę pisania",
      active: brushGroupOpen,
      minWidth: 42,
      style: { width: 42, height: 22, minHeight: 22, paddingInline: 8 },
      onClick: () => setBrushGroupOpen((v) => !v),
    },
    brushExtras: brushTools.filter((name) => name !== activeBrushTool).map((name) => createSideToolDef(name, "side-brush")),
    shapeMain: createSideToolDef(activeShapeTool, "side-shape-main"),
    shapeToggle: {
      key: "shape-toggle",
      label: shapeGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />,
      title: shapeGroupOpen ? "Zwiń grupę kształtów" : "Rozwiń grupę kształtów",
      active: shapeGroupOpen,
      minWidth: 42,
      style: { width: 42, height: 22, minHeight: 22, paddingInline: 8 },
      onClick: () => setShapeGroupOpen((v) => !v),
    },
    shapeExtras: shapeTools.filter((name) => name !== activeShapeTool).map((name) => createSideToolDef(name, "side-shape")),
    undo: { key: "undo", label: <UndoIcon />, title: "Cofnij", disabled: !canUndo, onClick: handleUndo },
    redo: { key: "redo", label: <RedoIcon />, title: "Ponow", disabled: !canRedo, onClick: handleRedo },
  };
  const fullSideButtonUnits = [
    [sideButtonDefs.select],
    [sideButtonDefs.brushMain, sideButtonDefs.brushToggle],
    ...(brushGroupOpen ? sideButtonDefs.brushExtras.map((btn) => [btn]) : []),
    [sideButtonDefs.shapeMain, sideButtonDefs.shapeToggle],
    ...(shapeGroupOpen ? sideButtonDefs.shapeExtras.map((btn) => [btn]) : []),
    ...normalizedExtraSideButtonDefs.map((btn) => [btn]),
    [sideButtonDefs.undo, sideButtonDefs.redo], // keep undo/redo together where possible
  ];
  const compactSideButtonUnits = [
    BASIC_SIDE_TOOLS.map((toolName) => (
      toolName === "select"
        ? sideButtonDefs.select
        : createSideToolDef(toolName, "side-compact-basic")
    )),
    ...normalizedExtraSideButtonDefs.map((btn) => [btn]),
  ];
  const sideButtonUnits = sideToolsExpanded ? fullSideButtonUnits : compactSideButtonUnits;

  const sideButtonColumns = [];
  let currentColumn = [];
  let currentRows = 0;

  const flushColumn = () => {
    if (!currentColumn.length) return;
    sideButtonColumns.push(currentColumn);
    currentColumn = [];
    currentRows = 0;
  };

  const currentRowsLimit = () => (sideButtonColumns.length === 0 ? maxRowsPrimaryColumn : maxRowsSecondaryColumn);

  const pushButtonWithWrapping = (btn) => {
    let limit = currentRowsLimit();
    if (currentRows > 0 && currentRows + 1 > limit) {
      flushColumn();
      limit = currentRowsLimit();
    }
    currentColumn.push(btn);
    currentRows += 1;
  };

  for (const unit of sideButtonUnits) {
    const unitRows = unit.length;
    const limit = currentRowsLimit();

    if (currentRows > 0 && currentRows + unitRows > limit) {
      flushColumn();
    }

    const postFlushLimit = currentRowsLimit();
    if (unitRows <= postFlushLimit) {
      unit.forEach((btn) => pushButtonWithWrapping(btn));
      continue;
    }

    // Very small viewport fallback: split oversized units.
    unit.forEach((btn) => pushButtonWithWrapping(btn));
  }

  flushColumn();
  const renderedSideButtonColumns = controlsSide === "left"
    ? sideButtonColumns
    : sideButtonColumns.slice().reverse();

  const quickSideControls = renderedSideButtonColumns.length > 1 ? (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      {renderedSideButtonColumns.map((column, columnIndex) => (
        <div key={`inline-side-col-${columnIndex}`} style={{ display: "grid", gap: 8 }}>
          {column.map((btn) => (
            <QuickIconButton
              key={btn.key}
              label={btn.label}
              title={btn.title}
              active={!!btn.active}
              indicatorColor={btn.indicatorColor}
              disabled={!!btn.disabled}
              minWidth={Number(btn.minWidth || 42)}
              style={btn.style || undefined}
              onClick={btn.onClick}
              cornerActionTitle={btn.cornerActionTitle || ""}
              onCornerAction={btn.onCornerAction}
              cornerActionActive={!!btn.cornerActionActive}
              cornerActionLabel={btn.cornerActionLabel || "⋯"}
            />
          ))}
        </div>
      ))}
    </div>
  ) : (
    <div style={{ display: "grid", gap: 8 }}>
      {sideButtonUnits.flat().map((btn) => (
        <QuickIconButton
          key={btn.key}
          label={btn.label}
          title={btn.title}
          active={!!btn.active}
          indicatorColor={btn.indicatorColor}
          disabled={!!btn.disabled}
          minWidth={Number(btn.minWidth || 42)}
          style={btn.style || undefined}
          onClick={btn.onClick}
          cornerActionTitle={btn.cornerActionTitle || ""}
          onCornerAction={btn.onCornerAction}
          cornerActionActive={!!btn.cornerActionActive}
          cornerActionLabel={btn.cornerActionLabel || "⋯"}
        />
      ))}
    </div>
  );

  const ribbonZoomInsetPx = 320;
  const ribbonPanelInsetPx = 128;
  const ribbonDockLeftInsetPx = useCompactControlsLayout
    ? 12
    : controlsSide === "left"
      ? ribbonZoomInsetPx
      : ribbonPanelInsetPx;
  const ribbonDockRightInsetPx = useCompactControlsLayout
    ? 12
    : controlsSide === "left"
      ? ribbonPanelInsetPx
      : ribbonZoomInsetPx;
  const ribbonDockWidth = useCompactControlsLayout
    ? "calc(100% - 24px)"
    : `min(1414px, calc(100% - ${ribbonDockLeftInsetPx + ribbonDockRightInsetPx}px))`;

  const quickToolConfigPopup = quickConfigTool ? (
    <div
      style={{
        position: "absolute",
        top: 74,
        ...(controlsSide === "right" ? { right: 118 } : { left: 118 }),
        zIndex: 35,
        width: 232,
        borderRadius: 14,
        border: themeMode === "light" ? "1px solid rgba(15,23,42,0.16)" : "1px solid rgba(255,255,255,0.16)",
        background: themeMode === "light" ? "rgba(235,248,255,0.96)" : "rgba(10,16,36,0.94)",
        boxShadow: "0 14px 36px rgba(0,0,0,0.32)",
        backdropFilter: "blur(10px)",
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 10,
        paddingRight: 6,
        display: "grid",
        gap: 10,
        pointerEvents: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>
          {getToolButtonMeta(quickConfigTool).title}
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setQuickToolConfigTool("")}
          onContextMenu={(e) => e.preventDefault()}
          style={{ width: 28, height: 28, minWidth: 28, padding: 0, ...touchSafeButtonStyle }}
          title="Zamknij"
        >
          ×
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {quickConfigPalette.map((c) => (
          <button
            key={`quick-tool-color-${quickConfigTool}-${c}`}
            type="button"
            onClick={() => setQuickToolColor(quickConfigTool, c)}
            onContextMenu={(e) => e.preventDefault()}
            title={c}
            aria-label={`Kolor ${c}`}
            className="btn btn-secondary"
            style={{
              width: 28,
              height: 28,
              minWidth: 28,
              borderRadius: 999,
              padding: 0,
              background: c,
              borderColor: quickConfigColor === c ? "#f59e0b" : "rgba(255,255,255,0.24)",
              boxShadow: quickConfigColor === c ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
              ...touchSafeButtonStyle,
            }}
          />
        ))}
        <RecentCustomColorButton
          color={lastCustomColor}
          palette={quickConfigPalette}
          size={28}
          active={String(quickConfigColor).toLowerCase() === String(lastCustomColor).toLowerCase()}
          onSelect={(nextColor) => setQuickToolColor(quickConfigTool, nextColor)}
        />
        <CustomColorPickerButton
          value={quickConfigColor}
          size={28}
          active={!quickConfigPalette.some((paletteColor) => paletteColor.toLowerCase() === String(quickConfigColor).toLowerCase())}
          onChange={(nextColor) => {
            setLastCustomColor(nextColor);
            setQuickToolColor(quickConfigTool, nextColor);
          }}
        />
      </div>

      <label
        style={{
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          gap: 8,
          alignItems: "center",
          fontSize: 12,
          fontWeight: 700,
          paddingRight: 0,
        }}
      >
        Grubość
        <input
          type="range"
          min={quickConfigRangeMin}
          max={quickConfigRangeMax}
          step={1}
          value={quickConfigSize}
          onChange={(e) => setQuickToolSize(quickConfigTool, Number(e.target.value))}
          style={{ flex: 1, minWidth: 0 }}
        />
        <span style={{ minWidth: 18, textAlign: "right" }}>{quickConfigSize}</span>
      </label>
    </div>
  ) : null;

  const bottomRightControls = (
    <QuickIconButton
      label={fullscreenActive ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
      title={fullscreenActive ? "Wyjdz z full screen" : "Full screen"}
      onClick={toggleFullscreen}
    />
  );

  const bottomRightTopControls = (
    <QuickIconButton
      label={<GridIcon />}
      title={gridEnabled ? "Wylacz kratke" : "Wlacz kratke"}
      active={gridEnabled}
      onClick={() => setGridEnabled((v) => !v)}
    />
  );
  const taskPreviewMaxHeight = Math.max(140, Math.min(320, Math.round(windowHeight * 0.3)));
  const taskPreview = fullscreenActive && hasTaskPreviewNode ? (
    <div
      ref={taskPreviewRef}
      style={{
        marginBottom: 10,
        position: "relative",
        borderRadius: 12,
        border: taskPreviewExpanded ? "1px solid rgba(255,255,255,0.16)" : "none",
        background: taskPreviewExpanded
          ? (themeMode === "light" ? "rgba(255,255,255,0.96)" : "rgba(12,16,40,0.9)")
          : "transparent",
        padding: taskPreviewExpanded ? 12 : 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 4,
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 60,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        <QuickIconButton
            label={taskPreviewExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          title={taskPreviewExpanded ? "Schowaj zadanie" : "Rozwiń zadanie"}
          onClick={() => setTaskPreviewExpanded((v) => !v)}
        />
      </div>

      {taskPreviewExpanded ? (
        <div style={{ display: "grid", gap: 8 }}>
          {(taskPreviewOverflow || taskPreviewFullHeight) ? (
            <div style={{ position: "absolute", top: 8, right: 10, zIndex: 3 }}>
              <QuickIconButton
                label={taskPreviewFullHeight ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
                title={taskPreviewFullHeight ? "Tryb skrótu zadania" : "Pełny widok zadania"}
                onClick={() => setTaskPreviewFullHeight((v) => !v)}
              />
            </div>
          ) : null}
          <div
            ref={taskPreviewBodyRef}
            style={{
              maxHeight: taskPreviewFullHeight ? "none" : taskPreviewMaxHeight,
              overflowY: taskPreviewFullHeight ? "visible" : "auto",
              overflowX: "hidden",
              paddingRight: taskPreviewFullHeight ? 0 : 4,
            }}
          >
            {taskPreviewNode}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  const boardContainer = (
    <div
      ref={fsRef}
      style={{
        marginTop: isPseudoFullscreen ? 0 : 10,
        paddingTop: 0,
        paddingLeft: fullscreenActive ? 12 : 0,
        paddingRight: fullscreenActive ? 12 : 0,
        paddingBottom: 0,
        background: fullscreenActive
          ? (themeMode === "light" ? "rgba(223,241,252,0.98)" : "#081020")
          : "transparent",
        width: isPseudoFullscreen ? "100vw" : "100%",
        minWidth: 0,
        maxWidth: isPseudoFullscreen ? "100vw" : "100%",
        flexBasis: "100%",
        boxSizing: "border-box",
        height: fullscreenActive ? `${Math.max(320, windowHeight)}px` : "auto",
        overflowY: fullscreenActive ? "hidden" : "visible",
        overflowX: "hidden",
        position: isPseudoFullscreen ? "fixed" : "relative",
        inset: isPseudoFullscreen ? 0 : "auto",
        zIndex: isPseudoFullscreen ? 1500 : "auto",
        borderRadius: isPseudoFullscreen ? 0 : (fullscreenActive ? 20 : 14),
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {taskPreview}
      {showExternalCompactControls ? (
        <div
          style={{
            marginBottom: 8,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            pointerEvents: "auto",
          }}
        >
          {compactTopControls}
          {compactExtraSideControls ? (
            <div
              style={{
                marginTop: 8,
                display: "flex",
                justifyContent: controlsSide === "left" ? "flex-start" : "flex-end",
              }}
            >
              {compactExtraSideControls}
            </div>
          ) : null}
        </div>
      ) : null}
      {isRibbonOpen ? (
        <div
          style={{
            width: ribbonDockWidth,
            position: "absolute",
            top: fullscreenActive ? 8 : -6,
            left: ribbonDockLeftInsetPx,
            right: ribbonDockRightInsetPx,
            margin: "0 auto",
            zIndex: 26,
            pointerEvents: "auto",
            maxHeight: fullscreenActive ? Math.max(220, windowHeight * 0.5) : 380,
            overflow: "auto",
          }}
        >
          {ribbonPanel}
        </div>
      ) : null}
      {quickToolConfigPopup}
      <WhiteboardCanvas
        readOnly={readOnly}
        value={state}
        onChange={handleBoardChange}
        onToolChange={setTool}
        onAddPage={handleAddPage}
        onPageChange={setCurrentPage}
        tool={tool}
        color={activeColor}
        brushSize={tool === "highlighter" ? highlighterSize : brushSize}
        overlay={null}
        topLeftControls={useCompactControlsLayout ? null : topLeftControls}
        topRightControls={useCompactControlsLayout ? (showExternalCompactControls ? null : compactTopControls) : topZoomControls}
        sideControls={useCompactControlsLayout ? null : quickSideControls}
        forceTopControlsDetached={useCompactControlsLayout && !showExternalCompactControls && !useOverlayCompactControls}
        keepTopControlsOverlayOnMobile={useOverlayCompactControls}
        controlsSide={controlsSide}
        zoomPercent={zoomPercent}
        onZoomPercentChange={setZoomPercent}
        preferFixedWidth={preferFixedWidthForCanvas}
        viewportHeightOverride={!fullscreenActive ? viewportHeightPx : null}
        fullscreenActive={fullscreenActive}
        fullscreenExtraOffset={fullscreenActive ? taskPreviewHeight + 14 + externalCompactControlsHeightPx : 0}
        floatingControlsBottomOffset={taskFloatingControlsBottomOffsetPx}
        themeMode={themeMode}
        boardSurface={boardSurface}
        bottomRightControls={bottomRightControls}
        bottomRightTopControls={bottomRightTopControls}
        bottomLeftTopControls={bottomLeftTopControls}
        gridEnabled={gridEnabled}
        enableElementSelection
        stylusOnly={stylusOnly}
      />
    </div>
  );

  return (
    <>
      {hideToggle ? null : (
        <div style={toggleDescription ? { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16,
          marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--color-border)", width: "100%" } : { display: "contents" }}>
        {toggleDescription && <p style={{ flex: "1 1 240px", margin: 0, fontSize: 14, lineHeight: 1.5 }}>{toggleDescription}</p>}
        <button
          className="btn-secondary"
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          onContextMenu={(e) => e.preventDefault()}
          title={isOpen ? "Zwin tablice" : "Otworz tablice"}
          aria-label={isOpen ? "Zwin tablice" : "Otworz tablice"}
          style={{
            minWidth: 52,
            width: 52,
            height: 36,
            padding: "0 8px",
            display: "grid",
            placeItems: "center",
            marginLeft: alignRight ? "auto" : undefined,
            flexShrink: 0,
            ...touchSafeButtonStyle,
          }}
        >
          <BoardToggleIcon open={isOpen} />
        </button>
        </div>
      )}

      {isOpen
        ? (isPseudoFullscreen && typeof document !== "undefined"
            ? createPortal(boardContainer, document.body)
            : boardContainer)
        : null}

      <MatematronModal
        open={clearConfirmOpen}
        variant="danger"
        title="Wyczyścić tablicę?"
        message="Ta operacja usunie wszystkie kreski i obrazy z tej tablicy."
        primaryText="Wyczyść"
        secondaryText="Anuluj"
        onPrimary={() => {
          if (!isBoardEmpty) clearBoardNow();
          setClearConfirmOpen(false);
        }}
        onSecondary={() => setClearConfirmOpen(false)}
        onClose={() => setClearConfirmOpen(false)}
      />

      {calcOpen ? (
        <EdgeDrawer
          side="left"
          isOpen={calcOpen}
          onOpenChange={setCalcOpen}
          width={420}
          title="Kalkulator maturalny"
          showHandle={false}
          offsetTop={0}
          background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
          contentMaxWidth={380}
          zIndex={sheetOpen ? 1602 : 1600}
          showBackdrop={!sheetOpen}
          headerRight={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setSheetOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10, ...touchSafeButtonStyle }}
              title="Otworz karte wzorow"
            >
              Karta wzorow
            </button>
          }
        >
          {calcOpen ? <ExamCalculator embedded /> : null}
        </EdgeDrawer>
      ) : null}

      {sheetOpen ? (
        <FormulaSheet
          isOpen={sheetOpen}
          onOpenChange={setSheetOpen}
          pdfPath={WZORY_URL}
          headerRightExtra={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCalcOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10, ...touchSafeButtonStyle }}
              title="Otworz kalkulator"
            >
              Kalkulator
            </button>
          }
        />
      ) : null}
    </>
  );
}
